import React, { useEffect, useMemo, useState } from 'react';
import { Alert, CircularProgress, Snackbar } from '@mui/material';
import {
  AccessTime,
  AutoAwesome,
  Bolt,
  Check,
  ChevronRight,
  FitnessCenter,
  FlagOutlined,
  Logout,
  PersonOutline,
  RestaurantMenu,
  SaveOutlined,
  Tune,
} from '@mui/icons-material';
import { useBeforeUnload, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './ProfileV2.css';

const previewProfile = {
  name: 'Marina',
  email: 'marina@nutrizia.es',
  birthDate: '1991-06-18',
  sex: 'Femenino',
  heightCM: 168,
  currentWeight: 64.2,
  comensalesCasa: 2,
  activityLevel: 'Moderadamente activo',
  goal: 'Mantener peso',
  mealsPerDay: 5,
  cookingSkillLevel: 3,
  cookingTime: '30 min',
  kitchenEquipment: ['Airfryer', 'Batidora', 'Horno'],
  dietaryPreferences: ['vegetariana'],
  allergies: ['frutos-secos'],
  dislikedFoods: ['marisco'],
  newRecipesInterest: true,
  wantsTracking: true,
  healthAppIntegration: false,
  macros: { kilocalories: 2140, carbs: 248, protein: 126, fat: 72 },
};

const previewOptions = {
  dietary: [{ id: 'vegetariana', label: 'Vegetariana flexible' }, { id: 'sin-gluten', label: 'Sin gluten' }],
  allergies: [{ id: 'frutos-secos', label: 'Frutos secos' }, { id: 'lactosa', label: 'Lactosa' }],
  disliked: [{ id: 'marisco', label: 'Marisco' }, { id: 'setas', label: 'Setas' }],
};

const toId = (value) => value?._id || value?.id || value;
const asNumberOrEmpty = (value) => (value === '' || value == null ? '' : Number(value));
const normalizeProfile = (data = {}) => ({
  name: data.name || '',
  birthDate: data.birthDate ? new Date(data.birthDate).toISOString().slice(0, 10) : '',
  sex: data.sex || '',
  heightCM: data.heightCM || '',
  currentWeight: data.currentWeight?.$numberDecimal || data.currentWeight || '',
  comensalesCasa: data.comensalesCasa || 1,
  activityLevel: data.activityLevel || '',
  goal: data.goal || '',
  mealsPerDay: data.mealsPerDay || 3,
  cookingSkillLevel: data.cookingSkillLevel || 1,
  cookingTime: data.cookingTime || '',
  kitchenEquipment: Array.isArray(data.kitchenEquipment) ? data.kitchenEquipment : [],
  dietaryPreferences: (data.dietaryPreferences || []).map(toId),
  allergies: (data.allergies || []).map(toId),
  dislikedFoods: (data.dislikedFoods || []).map(toId),
  newRecipesInterest: Boolean(data.newRecipesInterest),
  wantsTracking: Boolean(data.wantsTracking),
  healthAppIntegration: Boolean(data.healthAppIntegration),
  macros: {
    kilocalories: data.macros?.kilocalories || '',
    carbs: data.macros?.carbs || '',
    protein: data.macros?.protein || '',
    fat: data.macros?.fat || '',
  },
});

const Field = ({ label, hint, children, className = '' }) => <label className={`profile-v2-field ${className}`}>
  <span>{label}</span>
  {children}
  {hint && <small>{hint}</small>}
</label>;

const Toggle = ({ checked, onChange, title, description }) => <button
  type="button"
  role="switch"
  aria-checked={checked}
  className={`profile-v2-toggle-row ${checked ? 'is-on' : ''}`}
  onClick={() => onChange(!checked)}
>
  <span><strong>{title}</strong><small>{description}</small></span>
  <i><b /></i>
</button>;

const MultiChoice = ({ options, value, onChange, emptyText }) => <div className="profile-v2-choices">
  {options.length ? options.map((option) => {
    const selected = value.includes(option.id);
    return <button type="button" key={option.id} className={selected ? 'is-selected' : ''} onClick={() => onChange(selected ? value.filter((id) => id !== option.id) : [...value, option.id])}>
      {selected && <Check />} {option.label}
    </button>;
  }) : <small>{emptyText}</small>}
</div>;

export default function ProfileV2({ preview = false }) {
  const { authToken, logout } = useAuth();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [email, setEmail] = useState(preview ? previewProfile.email : '');
  const [form, setForm] = useState(preview ? normalizeProfile(previewProfile) : null);
  const [original, setOriginal] = useState(preview ? normalizeProfile(previewProfile) : null);
  const [options, setOptions] = useState(preview ? previewOptions : { dietary: [], allergies: [], disliked: [] });
  const [loading, setLoading] = useState(!preview);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (preview) return;
    const headers = { Authorization: `Bearer ${authToken}` };
    const load = async () => {
      try {
        const userResponse = await fetch(`${import.meta.env.VITE_API_URL}/user`, { headers });
        if (!userResponse.ok) throw new Error('No se pudo obtener el usuario');
        const user = await userResponse.json();
        setUserId(user.id);
        const [profileResponse, accountResponse, allergensResponse, categoriesResponse, dietaryResponse] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/userinfo/user/${user.id}`, { headers }),
          fetch(`${import.meta.env.VITE_API_URL}/user/profile`, { headers }),
          fetch(`${import.meta.env.VITE_API_URL}/allergens`, { headers }),
          fetch(`${import.meta.env.VITE_API_URL}/ingredient-categories`, { headers }),
          fetch(`${import.meta.env.VITE_API_URL}/restricciones`, { headers }),
        ]);
        if (!profileResponse.ok) throw new Error('No se pudo cargar el perfil');
        const [profile, account, allergens, categories, dietary] = await Promise.all([
          profileResponse.json(),
          accountResponse.ok ? accountResponse.json() : {},
          allergensResponse.ok ? allergensResponse.json() : [],
          categoriesResponse.ok ? categoriesResponse.json() : [],
          dietaryResponse.ok ? dietaryResponse.json() : [],
        ]);
        const normalized = normalizeProfile(profile);
        setEmail(account.email || '');
        setForm(normalized);
        setOriginal(normalized);
        setOptions({
          allergies: allergens.map((item) => ({ id: item._id, label: item.name })),
          disliked: categories.map((item) => ({ id: item._id, label: item.name })),
          dietary: dietary.map((item) => ({ id: item._id, label: item.name })),
        });
      } catch (error) {
        setNotice({ open: true, message: error.message || 'No se pudo cargar tu perfil', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authToken, preview]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original]);
  useBeforeUnload(React.useCallback((event) => {
    if (!dirty) return;
    event.preventDefault();
    event.returnValue = '';
  }, [dirty]));

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateMacro = (field, value) => setForm((current) => ({ ...current, macros: { ...current.macros, [field]: value } }));
  const initials = (form?.name || 'Tu perfil').split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase();

  const save = async () => {
    if (!String(form.name).trim()) {
      setNotice({ open: true, message: 'Añade tu nombre antes de guardar', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (!preview) {
        const payload = {
          ...form,
          heightCM: asNumberOrEmpty(form.heightCM),
          comensalesCasa: Number(form.comensalesCasa),
          mealsPerDay: Number(form.mealsPerDay),
          cookingSkillLevel: Number(form.cookingSkillLevel),
          macros: Object.fromEntries(Object.entries(form.macros).map(([key, value]) => [key, asNumberOrEmpty(value)])),
        };
        delete payload.currentWeight;
        const response = await fetch(`${import.meta.env.VITE_API_URL}/userinfo/user/${userId}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error('No se pudieron guardar los cambios');
        const saved = await response.json();
        const normalized = normalizeProfile({ ...form, ...saved, currentWeight: form.currentWeight });
        setForm(normalized);
        setOriginal(normalized);
      } else {
        setOriginal(form);
      }
      setNotice({ open: true, message: 'Perfil actualizado correctamente', severity: 'success' });
    } catch (error) {
      setNotice({ open: true, message: error.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="profile-v2-loading"><CircularProgress /><span>Preparando tu perfil…</span></div>;
  if (!form) return <div className="profile-v2-loading"><strong>No hemos podido cargar tu perfil.</strong></div>;

  return <div className="profile-v2-page">
    <header className="profile-v2-hero">
      <div className="profile-v2-avatar" aria-hidden="true">{initials}</div>
      <div className="profile-v2-identity">
        <span className="profile-v2-kicker">TU ESPACIO PERSONAL</span>
        <h1>{form.name || 'Tu perfil'}</h1>
        <p>{email || 'Configura tus datos para personalizar Nutrizia'}</p>
      </div>
      <div className="profile-v2-hero-actions">
        <button type="button" className="profile-v2-ghost" onClick={() => document.getElementById('datos-personales')?.scrollIntoView({ behavior: 'smooth' })}><PersonOutline /> Editar datos</button>
        {!preview && <button type="button" className="profile-v2-logout" onClick={() => { logout(); navigate('/login'); }}><Logout /> Cerrar sesión</button>}
      </div>
      <div className="profile-v2-quick-facts">
        <div><RestaurantMenu /><span><strong>{form.mealsPerDay} comidas</strong><small>ritmo diario</small></span></div>
        <div><FlagOutlined /><span><strong>{form.goal || 'Sin definir'}</strong><small>objetivo</small></span></div>
        <div><Bolt /><span><strong>{form.macros.kilocalories || '—'} kcal</strong><small>objetivo diario</small></span></div>
      </div>
    </header>

    <div className="profile-v2-layout">
      <aside className="profile-v2-index" aria-label="Secciones del perfil">
        <span>Tu perfil</span>
        <a href="#planificacion"><RestaurantMenu /> Planificación <ChevronRight /></a>
        <a href="#objetivos"><FitnessCenter /> Objetivos <ChevronRight /></a>
        <a href="#datos-personales"><PersonOutline /> Datos personales <ChevronRight /></a>
        <a href="#preferencias"><Tune /> Preferencias <ChevronRight /></a>
        <a href="#cocina"><AccessTime /> Cocina y seguimiento <ChevronRight /></a>
      </aside>

      <main className="profile-v2-content">
        <section className="profile-v2-card profile-v2-planning" id="planificacion">
          <div className="profile-v2-section-heading"><span className="is-purple"><RestaurantMenu /></span><div><small>PLANIFICACIÓN</small><h2>Tu ritmo de comidas</h2><p>Usaremos esta preferencia al crear menús y repartir tus objetivos diarios.</p></div></div>
          <div className="profile-v2-meal-picker" role="group" aria-label="Comidas por día">
            {[3, 4, 5, 6].map((count) => <button type="button" key={count} className={Number(form.mealsPerDay) === count ? 'is-selected' : ''} onClick={() => update('mealsPerDay', count)}>
              <strong>{count}</strong><span>comidas</span>{Number(form.mealsPerDay) === count && <Check />}
            </button>)}
          </div>
          <div className="profile-v2-planning-note"><AutoAwesome /><span><strong>Una preferencia que trabaja por ti</strong><small>Adaptaremos desayunos, snacks y comidas principales sin que tengas que indicarlo cada vez.</small></span></div>
        </section>

        <section className="profile-v2-card" id="objetivos">
          <div className="profile-v2-section-heading"><span className="is-green"><FitnessCenter /></span><div><small>OBJETIVOS</small><h2>Nutrición y actividad</h2><p>Ajusta el punto de partida que usamos para tus recomendaciones.</p></div></div>
          <div className="profile-v2-form-grid two">
            <Field label="Objetivo"><select value={form.goal} onChange={(event) => update('goal', event.target.value)}><option value="">Selecciona un objetivo</option>{['Perder peso', 'Ganar masa muscular', 'Mantener peso', 'Mejorar salud general', 'Recomposición'].map((value) => <option key={value}>{value}</option>)}</select></Field>
            <Field label="Nivel de actividad"><select value={form.activityLevel} onChange={(event) => update('activityLevel', event.target.value)}><option value="">Selecciona tu actividad</option>{['Sedentario', 'Ligeramente activo', 'Moderadamente activo', 'Muy activo', 'Extremadamente activo'].map((value) => <option key={value}>{value}</option>)}</select></Field>
          </div>
          <div className="profile-v2-macros">
            {[['kilocalories', 'Calorías', 'kcal'], ['carbs', 'Carbohidratos', 'g'], ['protein', 'Proteínas', 'g'], ['fat', 'Grasas', 'g']].map(([key, label, unit]) => <Field key={key} label={label}><div className="profile-v2-unit-input"><input type="number" min="0" value={form.macros[key]} onChange={(event) => updateMacro(key, event.target.value)} /><span>{unit}</span></div></Field>)}
          </div>
        </section>

        <section className="profile-v2-card" id="datos-personales">
          <div className="profile-v2-section-heading"><span className="is-blue"><PersonOutline /></span><div><small>DATOS PERSONALES</small><h2>Sobre ti</h2><p>Solo pedimos lo necesario para afinar cálculos y cantidades.</p></div></div>
          <div className="profile-v2-form-grid three">
            <Field label="Nombre" className="profile-v2-span-two"><input value={form.name} onChange={(event) => update('name', event.target.value)} /></Field>
            <Field label="Email" hint="El email de acceso se gestiona desde tu cuenta"><input value={email} disabled /></Field>
            <Field label="Fecha de nacimiento"><input type="date" value={form.birthDate} onChange={(event) => update('birthDate', event.target.value)} /></Field>
            <Field label="Sexo"><select value={form.sex} onChange={(event) => update('sex', event.target.value)}><option value="">Sin especificar</option>{['Masculino', 'Femenino', 'Otro'].map((value) => <option key={value}>{value}</option>)}</select></Field>
            <Field label="Altura"><div className="profile-v2-unit-input"><input type="number" min="100" max="220" value={form.heightCM} onChange={(event) => update('heightCM', event.target.value)} /><span>cm</span></div></Field>
            <Field label="Peso actual" hint="Se actualiza desde Mediciones"><div className="profile-v2-unit-input"><input value={form.currentWeight || ''} disabled /><span>kg</span></div></Field>
            <Field label="Comensales en casa"><select value={form.comensalesCasa} onChange={(event) => update('comensalesCasa', Number(event.target.value))}>{Array.from({ length: 10 }, (_, index) => index + 1).map((value) => <option key={value}>{value}</option>)}</select></Field>
          </div>
        </section>

        <section className="profile-v2-card" id="preferencias">
          <div className="profile-v2-section-heading"><span className="is-orange"><Tune /></span><div><small>PREFERENCIAS</small><h2>Lo que te sienta bien</h2><p>Estas selecciones se tienen en cuenta al proponerte recetas.</p></div></div>
          <div className="profile-v2-choice-group"><strong>Tipo de alimentación</strong><MultiChoice options={options.dietary} value={form.dietaryPreferences} onChange={(value) => update('dietaryPreferences', value)} emptyText="No hay opciones disponibles" /></div>
          <div className="profile-v2-choice-group"><strong>Alergias</strong><MultiChoice options={options.allergies} value={form.allergies} onChange={(value) => update('allergies', value)} emptyText="No hay alérgenos disponibles" /></div>
          <div className="profile-v2-choice-group"><strong>Alimentos que prefieres evitar</strong><MultiChoice options={options.disliked} value={form.dislikedFoods} onChange={(value) => update('dislikedFoods', value)} emptyText="No hay categorías disponibles" /></div>
        </section>

        <section className="profile-v2-card" id="cocina">
          <div className="profile-v2-section-heading"><span className="is-pink"><AccessTime /></span><div><small>EXPERIENCIA</small><h2>Cocina y seguimiento</h2><p>Personaliza la dificultad y el tiempo de las recetas que ves.</p></div></div>
          <div className="profile-v2-form-grid two">
            <Field label="Nivel en la cocina"><select value={form.cookingSkillLevel} onChange={(event) => update('cookingSkillLevel', Number(event.target.value))}>{[['1', 'Principiante'], ['2', 'Básico'], ['3', 'Intermedio'], ['4', 'Avanzado'], ['5', 'Chef']].map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
            <Field label="Tiempo habitual para cocinar"><select value={form.cookingTime} onChange={(event) => update('cookingTime', event.target.value)}><option value="">Sin preferencia</option>{['15 min', '30 min', '1 hora', 'más de 1 hora'].map((value) => <option key={value}>{value}</option>)}</select></Field>
            <Field label="Equipamiento" hint="Separa cada elemento con una coma" className="profile-v2-span-two"><input value={form.kitchenEquipment.join(', ')} onChange={(event) => update('kitchenEquipment', event.target.value.split(',').map((item) => item.trim()).filter(Boolean))} placeholder="Horno, airfryer, batidora…" /></Field>
          </div>
          <div className="profile-v2-toggles">
            <Toggle checked={form.newRecipesInterest} onChange={(value) => update('newRecipesInterest', value)} title="Descubrir recetas nuevas" description="Prioriza variedad frente a repetir recetas conocidas." />
            <Toggle checked={form.wantsTracking} onChange={(value) => update('wantsTracking', value)} title="Seguimiento de progreso" description="Activa las métricas corporales y su evolución." />
            <Toggle checked={form.healthAppIntegration} onChange={(value) => update('healthAppIntegration', value)} title="Integración con app de salud" description="Deja preparada la sincronización de datos de actividad." />
          </div>
        </section>
      </main>
    </div>

    <div className={`profile-v2-savebar ${dirty ? 'is-visible' : ''}`} aria-hidden={!dirty}>
      <span><strong>Tienes cambios sin guardar</strong><small>Se aplicarán a tus próximas recomendaciones.</small></span>
      <div><button type="button" onClick={() => setForm(original)}>Descartar</button><button type="button" className="is-primary" disabled={saving} onClick={save}>{saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />} Guardar cambios</button></div>
    </div>
    <Snackbar open={notice.open} autoHideDuration={4000} onClose={() => setNotice((current) => ({ ...current, open: false }))}><Alert severity={notice.severity} onClose={() => setNotice((current) => ({ ...current, open: false }))}>{notice.message}</Alert></Snackbar>
  </div>;
}
